import React, { useState } from "react";
import { FaUser, FaLock, FaKey, FaEye, FaEyeSlash } from "react-icons/fa"; // Import Eye icons
import axios from "axios";
import "./ForgotPassword.css"; // Optional: for custom styles
import { useNavigate } from "react-router-dom";
import { validatePassword } from "../../utils/passwordValidation"; // Adjust path as needed
import Swal from "sweetalert2"; // For better alerts

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState(""); // Add confirm password state
  const [otpSent, setOtpSent] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]); // State for password validation errors
  const [confirmPasswordError, setConfirmPasswordError] = useState(""); // State for confirm password error
  const [isSubmitting, setIsSubmitting] = useState(false); // To prevent multiple submissions
  const navigate = useNavigate();

  // State for toggling password visibility
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);


  const handleRequestOTP = async () => {
    if (!email.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please enter your email address.',
        width: '300px',
         confirmButtonColor:"green"
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/auth/request-otp`, { email });
      if (res.data.message === "OTP sent successfully") {
        setOtpSent(true);
        Swal.fire({
          icon: 'success',
          title: 'OTP Sent!',
          text: 'An OTP has been sent to your email address.',
          width: '300px',
          confirmButtonColor:"green"
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: res.data.message || 'Failed to send OTP. Please try again.',
          width: '300px',
           confirmButtonColor:"red"
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Email not found or an error occurred.";
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        width: '300px',
         confirmButtonColor:"red"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyOtp = async (enteredOtp) => {
    setVerifyingOtp(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/auth/verify-otp`, { email, otp: enteredOtp });
      if (res.data.message === "OTP verified successfully") {
        setOtpVerified(true);
        Swal.fire({
          icon: 'success',
          title: 'OTP Verified!',
          text: 'You can now set your new password.',
          width: '300px',
          confirmButtonColor:"green"
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Invalid OTP',
          text: res.data.message || 'Invalid OTP. Please try again.',
          width: '300px',
          confirmButtonColor:"green"
        });
        setOtpVerified(false); // Keep it false if verification fails
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "OTP verification failed. Please check the OTP and try again.";
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        width: '300px',
      });
      setOtpVerified(false); // Keep it false on error
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleOtpChange = (e) => {
    const val = e.target.value;
    setOtp(val);
    if (val.length === 6) {
      verifyOtp(val);
    }
  };

  // Handle new password input change and run validation
  const handleNewPasswordChange = (e) => {
    const val = e.target.value;
    setNewPassword(val);
    const errors = validatePassword(val);
    setPasswordErrors(errors);

    // Also check confirm password if newPassword changes
    if (confirmNewPassword && val !== confirmNewPassword) {
      setConfirmPasswordError("Passwords do not match.");
    } else {
      setConfirmPasswordError("");
    }
  };

  // Handle confirm new password input change
  const handleConfirmNewPasswordChange = (e) => {
    const val = e.target.value;
    setConfirmNewPassword(val);
    if (val !== newPassword) {
      setConfirmPasswordError("Passwords do not match.");
    } else {
      setConfirmPasswordError("");
    }
  };

  const handleResetPassword = async () => {
    // Re-validate password before submission
    const currentPasswordErrors = validatePassword(newPassword);
    if (currentPasswordErrors.length > 0) {
      setPasswordErrors(currentPasswordErrors);
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        html: `Please fix the following password issues:<br>${currentPasswordErrors.map(err => `• ${err}`).join('<br>')}`,
        width: '350px',
      });
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setConfirmPasswordError("Passwords do not match.");
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'New password and confirm password do not match.',
        width: '300px',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/auth/reset-password`, {
        email,
        newPassword,
      });

      if (res.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Password reset successful. You can log in now.',
          width: '300px',
          confirmButtonColor:"green"
        }).then(() => {
          navigate("/login");
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Reset Failed',
          text: res.data.message || 'Password reset failed. Please try again.',
          width: '300px',
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Something went wrong while resetting password.";
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        width: '300px',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h2>Forgot Password</h2>

        {/* Email Input */}
        <div className="input-group">
          <FaUser className="icon" />
          <input
            type="email"
            placeholder="Enter registered email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={otpSent || isSubmitting}
          />
        </div>

        {/* Send OTP Button / OTP Input */}
        {!otpSent ? (
          <button className="forgotpassword-button" onClick={handleRequestOTP} disabled={isSubmitting}>
            {isSubmitting ? "Sending OTP..." : "Send OTP"}
          </button>
        ) : (
          <>
            {!otpVerified && (
              <>
                <div className="input-group">
                  <FaKey className="icon" />
                  <input
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    maxLength={6}
                    onChange={handleOtpChange}
                    disabled={verifyingOtp || isSubmitting}
                  />
                </div>
                {verifyingOtp && <p className="info">Verifying OTP...</p>}
              </>
            )}

            {/* New Password & Confirm Password Inputs */}
            {otpVerified && (
              <>
                <p className="success-message">OTP Verified ✅</p>
                <div className="input-group">
                  <FaLock className="icon" />
                  <input
                    type={showNewPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={handleNewPasswordChange}
                    disabled={isSubmitting}
                  />
                  {showNewPassword ? (
                    <FaEyeSlash className="toggle-password" onClick={() => setShowNewPassword(false)} />
                  ) : (
                    <FaEye className="toggle-password" onClick={() => setShowNewPassword(true)} />
                  )}
                </div>
                {passwordErrors.length > 0 && (
                  <ul className="error-list">
                    {passwordErrors.map((err, index) => (
                      <li key={index} className="error">{err}</li>
                    ))}
                  </ul>
                )}

                <div className="input-group">
                  <FaLock className="icon" />
                  <input
                    type={showConfirmNewPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmNewPassword}
                    onChange={handleConfirmNewPasswordChange}
                    disabled={isSubmitting}
                  />
                  {showConfirmNewPassword ? (
                    <FaEyeSlash className="toggle-password" onClick={() => setShowConfirmNewPassword(false)} />
                  ) : (
                    <FaEye className="toggle-password" onClick={() => setShowConfirmNewPassword(true)} />
                  )}
                </div>
                {confirmPasswordError && <p className="error">{confirmPasswordError}</p>}

                <button
                  className="forgotpassword-button"
                  onClick={handleResetPassword}
                  disabled={isSubmitting || passwordErrors.length > 0 || !newPassword || newPassword !== confirmNewPassword}
                >
                  {isSubmitting ? "Resetting Password..." : "Reset Password"}
                </button>
              </>
            )}
          </>
        )}

        <p className="register-link">
          <a href="/login">Back to login</a>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;